/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState } from 'react';
import { View, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';
import { Ionicons } from '@expo/vector-icons';

import SociollaText from '@components/sociolla/SociollaText';

import Colors from '@global/colors';
import { isWeb } from '@global/screens';

const propTypes = {
  count: PropTypes.number,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  containerStyle: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.number,
  ]),
  activeColor: PropTypes.string,
  bordered: PropTypes.bool,
  barHeight: PropTypes.number,
  titleComponent: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  title: PropTypes.string,
  titleBold: PropTypes.bool,
  titleStyle: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.number,
  ]),
  target: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  targetStyle: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.number,
  ]),
  gradeCount: PropTypes.number,
  grades: PropTypes.array,
  gradeHeight: PropTypes.number,
  lastGrade: PropTypes.bool,
  lastGradeText: PropTypes.string,
  titleRowMobile: PropTypes.bool,
  titleRowEndMobile: PropTypes.bool,
  leftSpace: PropTypes.bool,
  showPopup: PropTypes.bool,
  popupText: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const defaultProps = {
  count: 0,
  style: {},
  containerStyle: {},
  activeColor: Colors.blue0,
  bordered: false,
  barHeight: 0,
  titleComponent: false,
  title: '',
  titleBold: false,
  titleStyle: {},
  target: '',
  targetStyle: {},
  gradeCount: 0,
  grades: [],
  gradeHeight: 25,
  lastGrade: false,
  lastGradeText: '',
  titleRowMobile: false,
  titleRowEndMobile: false,
  leftSpace: false,
  showPopup: false,
  popupText: false,
};

const ProgressChart = (props) => {
  const [show, setShow] = useState(false);

  const onEvent = (isShow = false) => {
    setShow(isShow);
  };

  const {
    count = 0,
    style = {},
    containerStyle = {},
    activeColor = Colors.blue0,
    bordered = false,
    barHeight = 0,
    titleComponent = false,
    title = '',
    titleBold = false,
    titleStyle = {},
    target = '',
    targetStyle = {},
    gradeCount = 0,
    grades = [],
    gradeHeight = 25,
    lastGrade = false,
    lastGradeText = '',
    titleRowMobile = false,
    titleRowEndMobile = false,
    leftSpace = false,
    showPopup = false,
    popupText = false,
  } = props;

  const lastProgressTotal = 100;
  const withGrades = gradeCount > 0 && grades?.length;
  const highGrade = withGrades ? grades[grades.length - 1] : lastProgressTotal;
  const progressHigh =
    gradeCount > 0 && highGrade ? highGrade : lastProgressTotal;
  const progressNumber = Math.round(progressHigh / (progressHigh / count));

  let finalGrades = [];
  if (withGrades) {
    finalGrades = JSON.parse(JSON.stringify(grades));
    finalGrades.splice(grades.length - 1, 1);
    finalGrades.reverse();
    finalGrades.push(grades[grades.length - 1]);
  }

  let finalProgressNumber = progressNumber;
  if (withGrades && highGrade > lastProgressTotal && grades?.length > 1) {
    if (progressNumber > progressHigh) {
      finalProgressNumber = lastProgressTotal;
    } else {
      const totalGrades = grades.length - 1;
      const progressSpace = progressHigh - (progressHigh - totalGrades * 15);
      if (progressNumber > lastProgressTotal) {
        const spaceValue = (progressHigh - lastProgressTotal) * totalGrades;
        const progressSpaceValue =
          (progressNumber - lastProgressTotal) * totalGrades;
        const moreSpaceValue = spaceValue / (spaceValue / progressSpaceValue);
        finalProgressNumber = Math.round(
          progressNumber - (progressSpace - moreSpaceValue),
        );
      } else {
        finalProgressNumber = progressNumber - progressSpace;
      }
    }
  }

  const dynamicProps = {
    ...(isWeb
      ? {
          onMouseEnter: () => onEvent(true),
          onMouseLeave: () => onEvent(false),
        }
      : {
          onStartShouldSetResponder: () => true,
          onResponderStart: () => onEvent(true),
        }),
  };

  return (
    <View
      style={[
        styles.progressWrapper,
        containerStyle,
        bordered ? styles.bordered : {},
        withGrades || lastGrade ? styles.moreMrVertical : {},
      ]}>
      {title || target ? (
        <View
          style={[
            styles.title,
            isWeb || titleRowMobile ? styles.titleRow : {},
            titleRowEndMobile ? styles.titleRowEnd : {},
          ]}>
          {titleComponent ? (
            titleComponent
          ) : title ? (
            <SociollaText
              type={titleBold ? 'medium' : 'regular'}
              style={[styles.titleText, titleStyle]}>
              {title}
            </SociollaText>
          ) : (
            false
          )}
          {target ? (
            <SociollaText
              type="medium"
              style={[styles.targetText, { color: activeColor }, targetStyle]}>
              {target}
            </SociollaText>
          ) : (
            false
          )}
        </View>
      ) : (
        false
      )}
      <View style={[styles.progress, barHeight ? { height: barHeight } : {}]}>
        {/* Progress Colored Start */}
        <View
          style={[
            styles.progress,
            styles.progressActive,
            { backgroundColor: activeColor },
            style,
            barHeight ? { height: barHeight } : {},
            { width: `${finalProgressNumber}%` },
          ]}
          {...dynamicProps}>
          {/* Progress Percentage Start */}
          {progressNumber < lastProgressTotal && !withGrades && !lastGrade ? (
            <View
              style={[
                styles.progressText,
                leftSpace ? styles.leftAuto : {},
                barHeight ? { top: 10 + barHeight } : {},
              ]}>
              <SociollaText type="medium" style={styles.progressTextNumber}>
                {progressNumber}%
              </SociollaText>
            </View>
          ) : (
            false
          )}
          {/* Progress Percentage End */}
          {/* Custom Popup Start */}
          {showPopup && popupText && show ? (
            <View
              style={[
                styles.progressText,
                styles.progressPopupText,
                leftSpace ? styles.leftAuto : {},
                { top: barHeight ? -barHeight + -25 : -25 },
              ]}>
              <SociollaText
                type="medium"
                style={styles.progressPopupTextNumber}
                numberOfLines={1}>
                {popupText}
              </SociollaText>
            </View>
          ) : (
            false
          )}
          {/* Custom Popup End */}
        </View>
        {/* Progress Colored End */}

        {/* Last Percentage Start */}
        {!withGrades && !lastGrade ? (
          <View
            style={[
              styles.progressText,
              barHeight ? { top: 10 + barHeight } : {},
            ]}>
            <SociollaText
              type={progressNumber >= lastProgressTotal ? 'medium' : 'regular'}
              style={styles.progressTextNumber}>
              {lastProgressTotal}%
            </SociollaText>
          </View>
        ) : (
          false
        )}
        {/* Last Percentage Start */}

        {/* Multiple Grades Start */}
        {withGrades ? (
          <>
            {finalGrades.map((item, idx) => (
              <View
                style={[
                  styles.gradeStyle,
                  progressNumber >= item
                    ? { backgroundColor: activeColor }
                    : {},
                  {
                    top: -((gradeHeight - (barHeight ? barHeight : 6)) / 2),
                    left: `${
                      idx == grades.length - 1
                        ? lastProgressTotal - 3
                        : lastProgressTotal - 3 - (idx + 1) * 15
                    }%`,
                    height: gradeHeight,
                    width: gradeHeight,
                    borderRadius: gradeHeight,
                  },
                ]}
                key={idx}>
                <View
                  style={[
                    styles.progressText,
                    styles.gradeTopProgressText,
                    barHeight ? { top: -(8 + barHeight) } : {},
                    idx == grades.length - 1
                      ? styles.gradeTopProgressTextLast
                      : {},
                  ]}>
                  <SociollaText style={styles.progressTextNumber}>
                    Grade {idx + 1}
                  </SociollaText>
                </View>
                {progressNumber >= item ? (
                  <Ionicons name="checkmark" size={15} color={Colors.white} />
                ) : (
                  false
                )}
                <View
                  style={[
                    styles.progressText,
                    styles.gradeProgressText,
                    barHeight ? { top: 20 + barHeight } : {},
                  ]}>
                  <SociollaText style={styles.progressTextNumber}>
                    {item}%
                  </SociollaText>
                </View>
              </View>
            ))}
          </>
        ) : (
          false
        )}
        {/* Multiple Grades End */}

        {/* Single Last Grades Start */}
        {lastGrade ? (
          <View
            style={[
              styles.gradeStyle,
              progressNumber >= lastProgressTotal
                ? { backgroundColor: activeColor }
                : {},
              {
                top: -((gradeHeight - (barHeight ? barHeight : 6)) / 2),
                height: gradeHeight,
                width: gradeHeight,
                borderRadius: gradeHeight,
              },
            ]}>
            {lastGradeText ? (
              <View
                style={[
                  styles.progressText,
                  styles.gradeTopProgressTextLast,
                  barHeight ? { top: -(8 + barHeight) } : {},
                ]}>
                <SociollaText style={styles.progressTextNumber}>
                  {lastGradeText}
                </SociollaText>
              </View>
            ) : (
              false
            )}
            {progressNumber >= lastProgressTotal ? (
              <Ionicons name="checkmark" size={15} color={Colors.white} />
            ) : (
              false
            )}
            <View
              style={[
                styles.progressText,
                styles.gradeProgressText,
                barHeight ? { top: 20 + barHeight } : {},
              ]}>
              <SociollaText style={styles.progressTextNumber}>
                {lastProgressTotal}%
              </SociollaText>
            </View>
          </View>
        ) : (
          false
        )}
        {/* Single Last Grades End */}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  title: {
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  titleRowEnd: {
    alignItems: 'flex-end',
  },
  progressWrapper: {
    position: 'relative',
    width: '100%',
    marginVertical: 10,
    paddingBottom: 35,
  },
  progress: {
    position: 'relative',
    width: '100%',
    height: 6,
    borderRadius: 10,
    backgroundColor: Colors.grey0,
  },
  progressActive: {
    position: 'absolute',
    height: 6,
    top: 0,
    left: 0,
  },
  progressText: {
    position: 'absolute',
    top: 10,
    right: 0,
  },
  progressPopupText: {
    backgroundColor: Colors.grey0,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 3,
  },
  progressTextNumber: {
    fontSize: isWeb ? 11 : 9,
  },
  progressPopupTextNumber: {
    fontSize: isWeb ? 13 : 10,
  },
  titleText: {
    fontSize: 13,
  },
  targetText: {
    fontSize: 16,
    color: Colors.blue0,
  },
  bordered: {
    borderBottomColor: Colors.grey0,
    borderBottomWidth: 1,
  },
  gradeStyle: {
    position: 'absolute',
    top: 0,
    left: '97%',
    zIndex: 1,
    elevation: 1,
    backgroundColor: Colors.grey0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  gradeProgressText: { right: isWeb ? -3 : 0 },
  gradeTopProgressText: { width: isWeb ? 45 : 36, right: isWeb ? -12 : -5 },
  gradeTopProgressTextLast: { right: 0 },
  moreMrVertical: { marginVertical: 15, width: isWeb ? '98%' : '96%' },
  leftAuto: { right: 'auto' },
});

ProgressChart.propTypes = propTypes;
ProgressChart.defaultProps = defaultProps;

export default React.memo(ProgressChart, (prev, next) => prev == next);
